import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Grid,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
} from '@mui/icons-material';
import { MdDeleteForever } from 'react-icons/md';
import { BsPlusCircleDotted } from 'react-icons/bs';
import { getReq, postReqfileupload } from '../../apis/api';
import FormButton from '../../components/Button';
import { enqueueSnackbar } from 'notistack';
import Loader from '../../components/loader/Loading';
import EditStagesModal from '../../components/Modal/editStagesmodal/EditStagesModal';
import { money, dateTime } from '../../utils/format'; // FIX_RULES 10.3: one money / date format
import { currentRoleId } from '../../utils/session';
import { splitLineSection } from '../../utils/shapeWeight';
import { FilterBar, SelectField } from '../../components/common/FilterControls';


// "YYYY-MM-DDTHH:MM" now on the browser's (Indian) clock - the latest a stage can be logged at
const nowLocal = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);

// SGEW 18 Oct: back to where the page was opened from - or, opened by a link / in a new tab, to the Sub-items list
const useGoBack = () => {
  const navigate = useNavigate();
  return () => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/items/subitems'));
};

// SGEW 18 Oct: stages are logged and edited by the Admin, Sub Admin and Supervisor only (Permissions: addSplitStages /
// updateSplitStages r:1,2,5) - every other role that can open the page sees the splits and stages without the form
const STAGE_LOGGERS = [1, 2, 5];

// the splits and stages of one sub-item (subitemId); `subitem` is its record when known (name and code in the heading)
const SubitemStages = ({ subitemId, subitem }) => {
  const canLog = STAGE_LOGGERS.includes(Number(currentRoleId()));
  const navigate = useNavigate();
  const goBack = useGoBack();
  const [employees, setEmployees] = useState([]);
  const [stageData, setStageData] = useState([]);
  const [allSplits, setAllSplits] = useState([]);
  const [filteredSplits, setFilteredSplits] = useState([]);
  const [, setItemsData] = useState([]);
  const [subitemData, setSubitemData] = useState([]);
  const [allStagesStatus, setAllStagesStatus] = useState({});
  const [splitStagesData, setSplitStagesData] = useState({});
  const [loading, setLoading] = useState(false);
  const [splitsLoaded, setSplitsLoaded] = useState(false); // SGEW 18 Oct: "no splits yet" only once the answer is in

  // --- State and Handlers for Split/Subitem Form ---
  const [subitemForm, setSubitemForm] = useState({});
  const [statusFilter, setStatusFilter] = useState('pending');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedStageData, setSelectedStageData] = useState(null);
  const [selectedSplitId, setSelectedSplitId] = useState(null);
  

  const handleSubitemChange = (splitId, subitemId, idx, field, value) => {
    setSubitemForm(prev => ({
      ...prev,
      [splitId]: {
        ...(prev[splitId] || {}),
        [subitemId]: (prev[splitId]?.[subitemId] || []).map((entry, i) =>
          i === idx ? { ...entry, [field]: value } : entry
        )
      }
    }));
  };

  const handleAddEmployeeEntry = (splitId, subitemId) => {
    setSubitemForm(prev => ({
      ...prev,
      [splitId]: {
        ...(prev[splitId] || {}),
        [subitemId]: [
          ...(prev[splitId]?.[subitemId] || []),
          { stage_id: '', emp_id: '', date_time: '', status: 'pending', quantity: '' }
        ]
      }
    }));
  };

  // SGEW 19 Oct (owner #2, "Fill remaining stages"): one line for every stage of this sub-item still short of the split
  // quantity - quantity = what is left (after the stages logged and the lines already on the form), employee = the split's
  // employee (else the last one who logged that stage of this sub-item), time = now. Only fills the form: the lines stay
  // editable, Save sends them and the Admin still approves.
  const remainingStages = (split, sub) => {
    const splitQty = Number(sub.quantity || 0);
    const done = {};
    (splitStagesData[`${split.split_id}_${sub.split_subitem}`] || [])
      .flatMap((s) => (Array.isArray(s.subitems) ? s.subitems : [s]))
      .concat((subitemForm[split.id]?.[sub.split_subitem] || []).filter((e) => e.stage_id))
      .forEach((e) => {
        done[String(e.stage_id)] = (done[String(e.stage_id)] || 0) + Number(e.quantity || 0);
      });
    return stageData
      .map((stage) => ({ stage, left: splitQty - (done[String(stage.id)] || 0) }))
      .filter((s) => s.left > 0);
  };

  const fillRemainingStages = (split, sub) => {
    const isEmployee = (id) => id !== undefined && id !== null && id !== '' && employees.some((emp) => String(emp.id) === String(id));
    // every stage logged for this sub-item on the page (the page lists only its splits and only its lines), newest last
    const logged = Object.values(splitStagesData)
      .flatMap((rows) => rows.flatMap((s) => (Array.isArray(s.subitems) ? s.subitems : [s])))
      .filter((e) => e.stage_id && isEmployee(e.emp_id))
      .sort((a, b) => String(a.date_time || '').localeCompare(String(b.date_time || '')));
    const lastFor = (stageId) => [...logged].reverse().find((e) => String(e.stage_id) === String(stageId))?.emp_id;
    const now = nowLocal();
    const lines = remainingStages(split, sub).map(({ stage, left }) => ({
      stage_id: stage.id,
      emp_id: isEmployee(sub.created_by) ? sub.created_by : lastFor(stage.id) || '',
      date_time: now,
      status: 'pending',
      quantity: left,
    }));
    if (!lines.length) {
      enqueueSnackbar('Every stage of this sub-item has its quantity logged or on the form.', { variant: 'info' });
      return;
    }
    setSubitemForm((prev) => ({
      ...prev,
      [split.id]: {
        ...(prev[split.id] || {}),
        // the lines already started (a stage chosen) stay; the blank starting line gives way to the filled ones
        [sub.split_subitem]: [...(prev[split.id]?.[sub.split_subitem] || []).filter((e) => e.stage_id), ...lines],
      },
    }));
  };

  const handleRemoveEmployeeEntry = (splitId, subitemId, idx) => {
    setSubitemForm(prev => {
      const updated = { ...prev };
      const arr = [...(updated[splitId]?.[subitemId] || [])];
      arr.splice(idx, 1);
      if (!updated[splitId]) updated[splitId] = {};
      updated[splitId][subitemId] = arr;
      return updated;
    });
  };

  const handleEditStage = (stageData, splitId) => {
    setSelectedStageData(stageData);
    setSelectedSplitId(splitId);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedStageData(null);
  };

  const handleStageUpdated = () => {
    fetchStageSplitData();
  };

  // Initialize subitemForm for all subitems if not already present
  useEffect(() => {
    allSplits.forEach(split => {
      (split.subitems || []).forEach(sub => {
        if (!subitemForm[split.id]?.[sub.split_subitem]) {
          setSubitemForm(prev => ({
            ...prev,
            [split.id]: {
              ...(prev[split.id] || {}),
              [sub.split_subitem]: [
                { stage_id: '', emp_id: '', date_time: '', status: 'pending', quantity: sub.quantity || '' }
              ]
            }
          }));
        }
      });
    });
    // eslint-disable-next-line
  }, [allSplits]);

  // SGEW 19 Oct (owner, "Reject split too"): a split rejected whole is "rejected" (its stages are closed); else its stages' status
  const statusOf = (split) => (split.status === 'rejected' ? 'rejected' : allStagesStatus[split.split_id] || 'pending');
  const isClosed = (split) => statusOf(split) === 'rejected';

  const applyStatusFilter = (filter, splits = allSplits) => {
    if (filter === 'all') {
      setFilteredSplits(splits);
    } else {
      const filtered = splits.filter(split => {
        const status = statusOf(split);
        if (filter === 'pending') {
          return ['pending', 'completed'].includes(status);
        }
        return status === filter;
      });
      setFilteredSplits(filtered);
    }
  };

  const handleSaveSplit = async (split) => {
    const isCompleted = isSplitCompleted(split);

    let subitemsArr = [];
    (split.subitems || []).forEach((sub) => {
      const entries = subitemForm[split.id]?.[sub.split_subitem] || [];

      const prevStages = (splitStagesData[split.split_id] || [])
        .flatMap(splitStageObj => Array.isArray(splitStageObj.subitems) ? splitStageObj.subitems : []);

      prevStages.forEach((prev) => {
        if (prev.stage_id && prev.quantity > 0) {
          subitemsArr.push({
            stage_id: String(prev.stage_id),
            quantity: String(prev.quantity),
            weight: String(sub.weight),
            emp_id: String(prev.emp_id || prev.employee_id || ''),
            date_time: String(prev.date_time || prev.date || ''),
            status: String(prev.status || 'completed'),
          });
        }
      });

      entries.forEach((form) => {
        if (form.stage_id && form.quantity > 0) {
          subitemsArr.push({
            stage_id: String(form.stage_id),
            quantity: String(form.quantity),
            weight: String(sub.weight),
            emp_id: String(form.emp_id),
            split_subitemid: String(sub.split_subitem),
            date_time: String(form.date_time),
            status: String(form.status),
          });
        }
      });
    });

    // SGEW 19 Oct (field audit): a save with no new stage line sent status=completed with no entries - the server's
    // override that completes EVERY sub-item of the split (also ones logged nowhere), so pressing Save Split on a finished
    // sub-item raised stage approvals for its neighbours. Nothing new = nothing sent; the server works out completion.
    if (subitemsArr.length === 0) {
      enqueueSnackbar(
        !isCompleted
          ? "Choose the stage, the employee and a quantity first."
          : allStagesStatus[split.split_id] === "completed"
            ? "Every stage of this sub-item is logged; it waits for the Admin's approval."
            : "Every stage is logged. To send them for approval again (after a rejection), correct a line with its Edit button and save it.",
        { variant: "info", autoHideDuration: 8000 }
      );
      return;
    }
    const missingEmployee = subitemsArr.some((e) => !e.emp_id);
    if (missingEmployee) {
      enqueueSnackbar("Choose who did each stage (Employee).", { variant: "warning" });
      return;
    }

    const formData = new FormData();
    formData.append('split_id', String(split.split_id));
    formData.append('item_id', String(split.item_id)); 
    formData.append('subitems', JSON.stringify(subitemsArr));
    formData.append('status', 'pending');

    try {
      const response = await postReqfileupload(formData, '/addSplitStages');
      if (response.status === 200) {
        const successMessage = response?.data?.message || "Subitem Split Successfully";
        const sent = !!response?.data?.sent_for_approval;

        enqueueSnackbar(successMessage, { variant: 'success', autoHideDuration: sent ? 8000 : 5000 });

        // SGEW 19 Oct (lead review, s2): only the Admin is taken to Approvals (the Admin's page); the Sub Admin and the
        // Supervisor who logged the last stage stay here - they landed on a page they cannot open
        if (sent && Number(currentRoleId()) === 1) {
          window.location.href = "/dashboard/approvals?module_type=stages";
        } else {
          fetchStageSplitData();
        }

      } else {
        enqueueSnackbar(response?.data?.message, { variant: 'warning' });
      }
    } catch (err) {
      // the server's reason (a stage over the split quantity, a date ahead...) - it said "Something Went Wrong"
      const status = err?.response?.status;
      if (status !== 403 && !(status >= 500)) {
        enqueueSnackbar(err?.response?.data?.message || "The stages could not be saved.", { variant: 'warning' });
      }
    }
  };

  // SGEW 19 Oct (field audit): each split's status arrives after the list - the Show filter is applied again then (an
  // approved split was listed under "Pending" because its status was not known yet when the filter ran)
  useEffect(() => {
    applyStatusFilter(statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-filters when the statuses come in
  }, [allStagesStatus]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await getReq('fetchEmployee');
        setEmployees(res.data || []);
      } catch (e) {
        setEmployees([]);
      }
    };
    fetchEmployees();
  }, []);

  const getStageDataById = async () => {
    try {
      const response = await getReq(`subitemStages/${subitemId}`);
      if (response.status === 200) {
        setStageData(response?.data?.stage_names || []);
      } else {
        setStageData([]);
      }
    } catch (error) {
      console.error("error", error);
      setStageData([]);
    }
  };

  const fetchStageSplitData = async () => {
    try {
      setLoading(true);
      const response = await getReq(`fetchSplit/${subitemId}`);
      if (response.status === 200) {
        const parsedSplits = (response?.data || []).map(split => ({
          ...split,
          subitems: Array.isArray(split.subitems)
            ? split.subitems
            : JSON.parse(split.subitems || '[]')
        }));
        setAllSplits(parsedSplits);
        applyStatusFilter(statusFilter, parsedSplits);
        
        parsedSplits.forEach(split => {
          split.subitems.forEach(subitem => {
            fetchStagesFromApi(split.split_id, subitem.split_subitem);
          });
        });
      } else {
        setAllSplits([]);
        setFilteredSplits([]);
      }
    } catch (error) {
      console.error("error", error);
      setAllSplits([]);
      setFilteredSplits([]);
    } finally {
      setLoading(false);
      setSplitsLoaded(true);
    }
  };

  const fetchStagesFromApi = async (split_id, split_subitemid) => {
    try {
      const response = await getReq(`fetchSplitStages/${split_id}/${split_subitemid}`);
      if (response.status === 200 && response.data) {
        setAllStagesStatus(prev => ({
          ...prev,
          [split_id]: response.data[0]?.status || 'pending'
        }));
      }
      JSON.parse(response.data.subitems || '[]');
    } catch (error) {
      console.error(`Error fetching stage data for split_id ${split_id}:`, error);
    }
  };

  useEffect(() => {
    if (subitemId) {
      getStageDataById();
      fetchStageSplitData(); // its per-split results load each split's stages (fetchStagesFromApi(split, sub-item))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loads the stage data when the subitem changes
  }, [subitemId]);

  useEffect(() => {
    const fetchAllSplitStages = async () => {
      const stagesObj = {};

      const allPromises = allSplits.flatMap(split => {
        return (split.subitems || []).map(async subitem => {
          try {
            const res = await getReq(`fetchSplitStages/${split.split_id}/${subitem.split_subitem}`);

            if (res.status === 200) {
              stagesObj[`${split.split_id}_${subitem.split_subitem}`] =
                Array.isArray(res.data) ? res.data : [];
            } else {
              stagesObj[`${split.split_id}_${subitem.split_subitem}`] = [];
            }
          } catch {
            stagesObj[`${split.split_id}_${subitem.split_subitem}`] = [];
          }
        });
      });

      await Promise.all(allPromises);
      setSplitStagesData(stagesObj);
    };

    if (allSplits.length > 0) {
      fetchAllSplitStages();
    }
  }, [allSplits]);

  const defaultStageEntries = useMemo(() => {
    const obj = {};
    stageData.forEach((stage) => {
      obj[stage] = [{ date: '', employee: '', quantity: '', status: 'pending' }];
    });
    return obj;
  }, [stageData]);

  const { reset } = useForm({
    defaultValues: {
      stageEntries: defaultStageEntries,
    },
  });

  const fetchAllItemsData = async () => {
    try {
      const response = await getReq("/getAllItem");
      if (response.status === 200) {
        setItemsData(response?.data);
      }
    } catch (error) {
      console.error("error", error);
    }
  };

  const fetchSubItemsData = async () => {
    try {
      const response = await getReq("/subitems");
      if (response.status === 200) {
        setSubitemData(response?.data);
      }
    } catch (error) {
      enqueueSnackbar("Error in fetching subitems data", { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchAllItemsData();
    fetchSubItemsData();
  }, []);

  function GetSubitemName(id) {
    const subitem = subitemData.find((item) => String(item.id) === String(id));
    return subitem ? subitem.subitem_name : id;
  }

  useEffect(() => {
    reset({
      stageEntries: defaultStageEntries,
    });
  }, [defaultStageEntries, reset]);

  const isSplitCompleted = (split) => {
    return (split.subitems || []).every((sub) => {
      const subitemQuantity = Number(sub.quantity || 0);
      if (subitemQuantity <= 0) return false;

      const compositeKey = `${split.split_id}_${sub.split_subitem}`; 
      const prevStages = splitStagesData[compositeKey] || []; 
      const formEntries = subitemForm[split.id]?.[sub.split_subitem] || []; 

      const allEntries = [
        ...prevStages.flatMap(stage => Array.isArray(stage.subitems) ? stage.subitems : [stage]),
        ...formEntries
      ];

      const stageQuantities = {};
      allEntries.forEach(entry => {
        if (entry.stage_id) {
          const stageId = String(entry.stage_id);
          stageQuantities[stageId] = (stageQuantities[stageId] || 0) + Number(entry.quantity || 0);
        }
      }); 

      return stageData.every(stage => { 
        const stageId = String(stage.id);
        const stageQty = stageQuantities[stageId] || 0; 
        return stageQty >= subitemQuantity;
      });
    });
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
        <Box display="flex" alignItems="center" sx={{ minWidth: 0 }}>
          <IconButton onClick={goBack} sx={{ mr: 1 }} aria-label="Back">
            <ArrowBack />
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={600}>
              Manage Stages for Subitem
            </Typography>
            {/* SGEW 18 Oct: which sub-item, and a way to pick another */}
            <Typography variant="body2" color="text.secondary" data-testid="stages-subitem">
              {subitem ? `${subitem.subitem_name}${subitem.subitem_code ? ` (${subitem.subitem_code})` : ''}` : `Sub-item #${subitemId}`}
              {' · '}
              <button type="button" className="text-[#0074ff] underline" onClick={() => navigate('/items/stages')}>
                Choose another sub-item
              </button>
            </Typography>
          </Box>
        </Box>
        {/* SGEW 17 Oct: the same filter look as every list (label above, 38px control) */}
        <Box sx={{ minWidth: 160, mr: '2vw' }}>
          <FilterBar>
            <SelectField
              label="Show"
              value={statusFilter}
              onChange={newFilter => {
                setStatusFilter(newFilter);
                applyStatusFilter(newFilter);
              }}
              allLabel={null}
              options={[
                { value: 'pending', label: 'Pending' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' },
                { value: 'all', label: 'All' },
              ]}
            />
          </FilterBar>
        </Box>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {!canLog && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="stages-view-only">
          View only: the Admin, Sub Admin and Supervisor log and edit stages.
        </Typography>
      )}

      {/* SGEW 18 Oct: say so when there is nothing to show (the page was blank) */}
      {splitsLoaded && allSplits.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }} data-testid="stages-empty">
          This sub-item has no splits yet. Split an item into it (Items, Split) and its stages are logged here.
        </Typography>
      )}
      {splitsLoaded && allSplits.length > 0 && filteredSplits.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }} data-testid="stages-empty">
          No {statusFilter === 'approved' ? 'approved' : 'pending'} splits of this sub-item. Choose "All" under Show to see
          every split.
        </Typography>
      )}

      {filteredSplits.map((split, splitIdx) => (
        <Card key={split.id} sx={{ mb: 4, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, textDecoration: 'underline', fontWeight: 700 }}>
              Split - #{split.split_id}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}><strong>Item Code:</strong> {split.item_code}</Grid>
              <Grid item xs={12} sm={4}><strong>Item Name:</strong> {split.item_name}</Grid>
              <Grid item xs={12} sm={4}><strong>Item Weight:</strong> {split.item_weight}</Grid>
              <Grid item xs={12} sm={4}><strong>SGEW Price:</strong> {money(split.sgew_price)}</Grid>
              <Grid item xs={12} sm={4}><strong>Actual Qty:</strong> {split.actual_qty}</Grid>
              {/* SGEW 19 Oct (owner #15): what approving the split's last stage approves - the item weight the split uses and its
                  scrap (offcuts, listed on the Scrap list once approved) */}
              <Grid item xs={12} sm={4} data-testid="split-weight-used">
                <strong>Item Weight Used:</strong> {split.split_weight !== null && split.split_weight !== undefined ? `${Number(split.split_weight)} kg` : '-'}
              </Grid>
              <Grid item xs={12} sm={4} data-testid="split-scrap">
                <strong>Scrap:</strong> {split.scrap_weight !== null && split.scrap_weight !== undefined ? `${Number(split.scrap_weight)} kg` : '-'}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography fontWeight={500} display="flex" alignItems="center" gap={1}>
                  Status:
                  <Box
                    component="span"
                    sx={{
                      px: 2,
                      py: 0.5,
                      borderRadius: '999px',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      boxShadow: 1,
                      backgroundColor:
                        isClosed(split)
                          ? '#fde8e8'
                          : ['completed', 'approved'].includes(allStagesStatus[split.split_id])
                          ? '#d0f2d8'
                          : allStagesStatus[split.split_id] === 'pending'
                            ? '#fff3cd'
                            : '#e0e0e0',
                      color:
                        isClosed(split)
                          ? '#b91c1c'
                          : ['completed', 'approved'].includes(allStagesStatus[split.split_id])
                          ? '#2e7d32'
                          : allStagesStatus[split.split_id] === 'pending'
                            ? '#ff9800'
                            : '#616161',
                    }}
                  >
                    {statusOf(split)}
                  </Box>
                </Typography>
              </Grid>
            </Grid>
            {isClosed(split) && (
              <Typography variant="body2" sx={{ mb: 2, color: '#b91c1c' }} data-testid="split-rejected-note">
                Split {split.split_id} was rejected: the item weight it held is free again and its stages are closed.
              </Typography>
            )}
            {split.status !== 'approved' && !isClosed(split) && allStagesStatus[split.split_id] !== 'approved' && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="split-one-click-note">
                When the Admin approves the last of its stages, split {split.split_id} is approved in the same click: its sub-items
                go into stock
                {split.split_weight !== null && split.split_weight !== undefined ? `, ${Number(split.split_weight)} kg of the item is used` : ''}
                {split.scrap_weight !== null && split.scrap_weight !== undefined ? ` and ${Number(split.scrap_weight)} kg of scrap goes on the Scrap list` : ''}.
              </Typography>
            )}

            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>Subitems</Typography>

            {split.subitems && split.subitems.map((sub, subIdx) => {
              const entries = subitemForm[split.id]?.[sub.split_subitem] || [];

              const prevStages = (splitStagesData[split.split_id] || [])
                .flatMap(splitStageObj => Array.isArray(splitStageObj.subitems) ? splitStageObj.subitems : []);
              const prevStageQtyMap = {};

              prevStages.forEach(stage => {
                if (!stage.stage_id) return;
                prevStageQtyMap[stage.stage_id] = (prevStageQtyMap[stage.stage_id] || 0) + Number(stage.quantity || 0);
              });

              const hasAnyEntry = prevStages.length > 0 || entries.length > 0;
              let allStagesFulfilled = false;
              if (hasAnyEntry) {
                allStagesFulfilled = true;
                for (let i = 1; i <= stageData.length; i++) {
                  const key = String(i);
                  const prevQty = prevStageQtyMap[key] || 0;
                  const requiredQty = Number(sub.quantity || 0);
                  if (prevQty < requiredQty) {
                    allStagesFulfilled = false;
                    break;
                  }
                }
              }

              return (
                <Box
                  key={sub.split_subitem || sub.subitem_id || subIdx}
                  sx={{
                    mb: 3,
                    p: 2,
                    border: '1px solid #ccc',
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                  }}
                >
                  <Typography fontWeight={500} mb={1}>
                    Subitem #{sub.split_subitem} ({GetSubitemName(sub.subitem_id)})
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}><strong>Split Quantity:</strong> {sub.quantity}</Grid>
                    <Grid item xs={12} sm={4}><strong>Weight:</strong> {sub.weight}</Grid>
                    {/* SGEW 28 Oct: a round / pipe / angle ... piece shows its section (Ø 63), a flat its thickness and width */}
                    {splitLineSection(sub) ? (
                      <Grid item xs={12} sm={4}><strong>Section (mm):</strong> {splitLineSection(sub)}</Grid>
                    ) : (
                      <>
                        <Grid item xs={12} sm={4}><strong>Thickness:</strong> {sub.thickness}</Grid>
                        <Grid item xs={12} sm={4}><strong>Width:</strong> {sub.width}</Grid>
                      </>
                    )}
                    <Grid item xs={12} sm={4}><strong>Length:</strong> {sub.length}</Grid>
                    <Grid item xs={12} sm={4}><strong>Scrap Weight (kg):</strong> {sub.scrap_weight_kg}</Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  {(() => {
                    const compositeKey = `${split.split_id}_${sub.split_subitem}`;
                    const prevStages = splitStagesData[compositeKey] || [];

                    return prevStages.length > 0 ? (
                      <Box mb={2}>
                        <Typography fontWeight={500} color="primary" mb={1}>
                          Previous Split Stages
                        </Typography>
                        <Grid container spacing={1} sx={{ background: '#f1f1f1', borderRadius: 1, p: 1 }}>
                          <Grid item xs={2}><strong>Stage</strong></Grid>
                          <Grid item xs={2}><strong>Employee</strong></Grid>
                          <Grid item xs={2}><strong>Date</strong></Grid>
                          <Grid item xs={2}><strong>Quantity</strong></Grid>
                          <Grid item xs={2}><strong>Status</strong></Grid>
                          {canLog && !isClosed(split) && allStagesStatus[split.split_id] !== 'approved' && (
                            <Grid item xs={2}><strong>Actions</strong></Grid>
                          )}
                        </Grid>
                        {prevStages.flatMap(stage =>
                          Array.isArray(stage.subitems)
                            ? stage.subitems
                            : [stage]
                        ).map((displayStage, idx) => (
                          <Grid container spacing={1} key={idx} sx={{ borderBottom: '1px solid #eee', p: 1 }}>
                            <Grid item xs={2}>{displayStage.stage_name}</Grid>
                            <Grid item xs={2}>{displayStage.employee_name}</Grid>
                            <Grid item xs={2}>{dateTime(displayStage.date_time)}</Grid>
                            <Grid item xs={2}>{displayStage.quantity}</Grid>
                            <Grid item xs={2}>{displayStage.status}</Grid>
                            {canLog && !isClosed(split) && allStagesStatus[split.split_id] !== 'approved' && (
                              <Grid item xs={2}>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditStage(displayStage, split.split_id)}
                                  sx={{ p: 0.5 }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Grid>
                            )}
                          </Grid>
                        ))}
                      </Box>
                    ) : null;
                  })()}

                  {canLog && !isClosed(split) && !['approved', 'completed'].includes(allStagesStatus[split.split_id]) && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      {/* SGEW 19 Oct (owner #2): one click adds a line for every stage still short (checked before Save) */}
                      {(() => {
                        const left = remainingStages(split, sub);
                        return (
                          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5} mb={2}>
                            <button
                              type="button"
                              className="sgew-btn sgew-btn-sm sgew-btn-outline"
                              disabled={!left.length}
                              onClick={() => fillRemainingStages(split, sub)}
                              data-testid="fill-remaining-stages"
                            >
                              Fill remaining stages
                            </button>
                            <Typography variant="body2" color="text.secondary">
                              {left.length
                                ? `Still short: ${left.map((s) => `${s.stage.name} ${s.left}`).join(', ')}`
                                : 'Every stage has its quantity logged or on the form.'}
                            </Typography>
                          </Box>
                        );
                      })()}
                      {(entries.length === 0 || !allStagesFulfilled) && (
                        entries.map((entry, entryIdx, arr) => {
                          const stageId = entry.stage_id;
                          const thisQty = Number(entry.quantity || 0);
                          const subitemQty = Number(sub.quantity || 0);

                          const compositeKey = `${split.split_id}_${sub.split_subitem}`;
                          const prevStages = splitStagesData[compositeKey] || [];

                          const prevQtyForStage = prevStages
                            .flatMap(stage => Array.isArray(stage.subitems) ? stage.subitems : [stage])
                            .filter(prev => String(prev.stage_id) === String(stageId))
                            .reduce((sum, prev) => sum + Number(prev.quantity || 0), 0);

                          const otherEntriesQty = entries
                            .filter((e, i) => i !== entryIdx && e.stage_id === stageId)
                            .reduce((sum, e) => sum + Number(e.quantity || 0), 0);

                          const totalAssignedQty = prevQtyForStage + otherEntriesQty;
                          const maxAllowed = Math.max(0, subitemQty - totalAssignedQty);

                          const exceeds = thisQty > maxAllowed;
                          return (
                            // on a phone the five fields stack, so each line gets a dashed rule under it to keep the lines apart
                            <Box key={entryIdx} sx={{ mb: { xs: 2, sm: 1 }, pb: { xs: 2, sm: 0 }, borderBottom: { xs: '1px dashed #d1d5db', sm: 'none' } }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={2}>
                                <TextField
                                  select
                                  label="Stage"
                                  value={entry.stage_id || ''}
                                  onChange={e => handleSubitemChange(split.id, sub.split_subitem, entryIdx, 'stage_id', e.target.value)}
                                  fullWidth
                                  size="small"
                                >
                                  {stageData.map((stage) => (
                                    <MenuItem key={stage.id} value={stage.id}>
                                      {stage.name}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>

                              <Grid item xs={12} sm={2}>
                                <TextField
                                  select
                                  label="Employee"
                                  value={entry.emp_id || ''}
                                  onChange={e => handleSubitemChange(split.id, sub.split_subitem, entryIdx, 'emp_id', e.target.value)}
                                  fullWidth
                                  size="small"
                                >
                                  {employees.map(emp => (
                                    <MenuItem key={emp.id} value={emp.id}>{emp.user_name}</MenuItem>
                                  ))}
                                </TextField>
                              </Grid>

                              <Grid item xs={12} sm={2}>
                                <TextField
                                  label="Date"
                                  type="datetime-local"
                                  value={entry.date_time || ''}
                                  onChange={e => handleSubitemChange(split.id, sub.split_subitem, entryIdx, 'date_time', e.target.value)}
                                  fullWidth
                                  size="small"
                                  inputProps={{ max: nowLocal() }} // work done: not ahead of now (blank = now)
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>

                              <Grid item xs={12} sm={2}>
                                <TextField
                                  label="Quantity"
                                  type="number"
                                  value={entry.quantity || ''}
                                  onChange={e => {
                                    let val = Number(e.target.value);
                                    if (val > maxAllowed) {
                                      val = maxAllowed > 0 ? maxAllowed : 0;
                                      enqueueSnackbar(`You can split only ${maxAllowed}, not more than this`, { variant: 'warning' });
                                    }
                                    handleSubitemChange(split.id, sub.split_subitem, entryIdx, 'quantity', val);
                                  }}
                                  fullWidth
                                  size="small"
                                  error={exceeds}
                                  helperText={exceeds ? `Max allowed for this stage: ${maxAllowed}` : ''}
                                  inputProps={{ min: 0, max: maxAllowed }}
                                />
                              </Grid>

                              {/* SGEW 19 Oct (field audit): no Status box on a new line - the server keeps every logged stage
                                  pending until the Admin approves (the box was sent and ignored); the sub-item completes by
                                  itself when each of its stages has the split quantity */}

                              <Grid item xs="auto" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton size="small" onClick={() => handleAddEmployeeEntry(split.id, sub.split_subitem)} color="primary">
                                  <BsPlusCircleDotted size={22} />
                                </IconButton>
                                {arr.length > 1 && (
                                  <IconButton size="small" onClick={() => handleRemoveEmployeeEntry(split.id, sub.split_subitem, entryIdx)}>
                                    <MdDeleteForever size={22} style={{ color: "#d0412e" }} />
                                  </IconButton>
                                )}
                              </Grid>
                            </Grid>
                            </Box>
                          );
                        })
                      )}
                    </>
                  )}
                </Box>
              );
            })}

            {canLog && !isClosed(split) && allStagesStatus[split.split_id] !== 'approved' && (
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <FormButton
                  type="button"
                  title="Save Split"
                  func={() => { 
                    handleSaveSplit(split);
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      ))}

      <EditStagesModal
        open={editModalOpen}
        handleClose={handleCloseEditModal}
        stageData={selectedStageData}
        split_id={selectedSplitId}  
        subitemID={subitemId}
        onStageUpdated={handleStageUpdated}
      />
    </Box>
  );
};

// a sub-item the Stages page can be opened for: enabled and with stages to log (the Sub-items list's Stages action)
const hasStages = (s) => s.status === 'Enable' && s.stages && String(s.stages) !== '0';

/**
 * SGEW 18 Oct: /items/stages?subitem=ID - the sub-item is in the address, so a reload or a shared link opens the same
 * page (it came only in the navigation state and was lost). The Sub-items list, the split view and Approvals add the
 * row as state too, which names the sub-item before its record loads. Without a sub-item the page asks for one.
 */
const ManageStages = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const goBack = useGoBack();
  const [searchParams, setSearchParams] = useSearchParams();
  const fromState = [].concat(location.state?.id ?? [])[0]; // an older caller sent [ids]
  const subitemId = String(searchParams.get('subitem') || fromState || '').trim();
  const valid = /^\d+$/.test(subitemId);

  const [record, setRecord] = useState(null); // the sub-item's own record (subitems/{id})
  const [missing, setMissing] = useState(false);
  const [options, setOptions] = useState(null); // the picker's sub-items (null while loading)

  // a link with the id only in the state gets it into the address (kept on reload)
  useEffect(() => {
    if (!searchParams.get('subitem') && fromState) {
      setSearchParams({ subitem: String(fromState) }, { replace: true, state: location.state });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per address
  }, [location.key]);

  useEffect(() => {
    setRecord(null);
    setMissing(false);
    if (!subitemId) return undefined;
    if (!valid) {
      setMissing(true);
      return undefined;
    }
    let live = true;
    getReq(`subitems/${subitemId}`)
      .then((res) => live && (res?.data ? setRecord(res.data) : setMissing(true)))
      .catch((err) => {
        if (!live) return;
        if (err?.response?.status === 404) setMissing(true);
        else enqueueSnackbar(err?.response?.data?.message || 'The sub-item could not be loaded.', { variant: 'error' });
      });
    return () => {
      live = false;
    };
  }, [subitemId, valid]);

  // the picker: every enabled sub-item with stages, by name
  useEffect(() => {
    if (subitemId && !missing) return undefined;
    let live = true;
    getReq('/subitems')
      .then((res) => {
        if (!live) return;
        const rows = (res?.data || []).filter(hasStages);
        rows.sort((a, b) => String(a.subitem_name).localeCompare(String(b.subitem_name)));
        setOptions(rows);
      })
      .catch(() => {
        if (!live) return;
        setOptions([]);
        enqueueSnackbar('The sub-items could not be loaded.', { variant: 'error' });
      });
    return () => {
      live = false;
    };
  }, [subitemId, missing]);

  if (subitemId && !missing) {
    // the row handed over names the sub-item until its record is in
    const named = record || (String(location.state?.id) === subitemId && location.state?.subitem_name ? location.state : null);
    return <SubitemStages key={subitemId} subitemId={subitemId} subitem={named} />;
  }

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={goBack} sx={{ mr: 1 }} aria-label="Back">
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" fontWeight={600}>
          Manage Stages for Subitem
        </Typography>
      </Box>
      <Divider sx={{ mb: 4 }} />
      <Card sx={{ maxWidth: 640, boxShadow: 3 }} data-testid="stages-picker">
        <CardContent>
          {missing && (
            <Typography color="error" sx={{ mb: 2 }}>
              {valid ? `Sub-item #${subitemId} was not found.` : 'This link does not name a sub-item.'} Choose one below.
            </Typography>
          )}
          <Typography fontWeight={500} sx={{ mb: 2 }}>
            Choose a sub-item to see its splits and stages
          </Typography>
          <Autocomplete
            options={options || []}
            loading={options === null}
            onChange={(e, v) => v && navigate(`/items/stages?subitem=${v.id}`, { state: v })}
            isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
            getOptionLabel={(o) => `${o.subitem_name}${o.subitem_code ? ` (${o.subitem_code})` : ''}`}
            noOptionsText={options === null ? 'Loading...' : 'No enabled sub-item has stages to log.'}
            renderInput={(params) => (
              <TextField {...params} label="Sub-item" placeholder="Search by name or code"
                helperText="Enabled sub-items that have stages (as the Stages action on the Sub-items list)." />
            )}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default ManageStages;